import { Toaster } from 'react-hot-toast';
import React, { useState, useEffect } from 'react';
import { HashRouter as Router, Routes, Route, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

import { StringerDashboard } from './components/StringerDashboard';
import { CustomerFeedback } from './components/CustomerFeedback';
import { RacketCollection } from './components/RacketCollection';
import { ClassManagementProfessor } from './components/ClassManagementProfessor';
import { ClassManagementCustomer } from './components/ClassManagementCustomer';
import { CustomerSingleClass } from './components/CustomerSingleClass';
import { ProfessorSingleClass } from './components/ProfessorSingleClass';
import { OpenAgenda } from './components/OpenAgenda';
import { LoginView } from './components/LoginView';
import { ResetPasswordView } from './components/ResetPasswordView';
import { UserManagement } from './components/UserManagement';
import { ClubManagement } from './components/ClubManagement';
import { LogsView } from './components/LogsView';
import { GlobalRadarNotification } from './components/GlobalRadarNotification';
import { FriendlyMatchGlobalNotification } from './components/FriendlyMatchGlobalNotification';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ProfileSettingsModal } from './components/ProfileSettingsModal';
import { FriendlyMatchRadar } from './components/FriendlyMatchRadar';
import pricingBgImg from './assets/pricing-bg.jpg';
import ernestoImg from './assets/miami-open-ernesto.jpg';
import racketCollectionImg from './assets/racket-collection.jpg';
import agendaAbertaImg from './assets/agenda-aberta-bg.jpg';
import brandLogo from './assets/techtennis-logo.png';
import gestaoUsuariosImg from './assets/gestao-usuarios-bg.jpg';
import tennisProfileImg from './assets/tennis-profile.png';
import buscarAmistosoImg from './assets/buscar-amistoso-bg.jpg';
import torneiosNewImg from './assets/tile_torneios_new_generated.png';
import gestaoClubesImg from './assets/gestao-clubes-bg.png';
import { Eye, EyeOff } from 'lucide-react';
import { RankingView } from './components/RankingView';
import { TournamentView } from './components/TournamentView';
import { CompetitionListView } from './components/CompetitionListView';
import { CompetitionsHub } from './components/CompetitionsHub';
import { Bell } from 'lucide-react';
import { ClassificationView } from './components/ClassificationView';
import { GlobalNotificationsModal } from './components/GlobalNotificationsModal';
import { PricingDashboard } from './components/PricingDashboard';
import { GlobalResults } from './pages/GlobalResults';
import giroBackground from './assets/tile_tennis_v2_resultados.png';
import treinoDesenvolvimentoImg from './assets/treino_desenvolvimento_360_tile.png';
import { TrainingPlanManager } from './components/TrainingPlanManager';

const ForceChangePassword = () => {
  const { changePassword } = useAuth();
  const [newPass, setNewPass] = useState('');
  const [confirmPass, setConfirmPass] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (newPass.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres.');
      return;
    }
    if (newPass !== confirmPass) {
      setError('As senhas não coincidem.');
      return;
    }
    setLoading(true);
    try {
      await changePassword(newPass);
    } catch (err: any) {
      setError(err.message || 'Erro ao alterar a senha.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '12px 16px',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '12px',
    color: 'white',
    fontSize: '16px',
    outline: 'none',
    transition: 'all 0.2s',
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      background: 'linear-gradient(135deg, #1e1a3b 0%, #151125 100%)'
    }}>
      <div style={{
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(20px)',
        padding: '40px',
        borderRadius: '24px',
        border: '1px solid rgba(255,255,255,0.1)',
        width: '100%',
        maxWidth: '400px',
      }}>
        <h2 style={{ color: 'white', marginBottom: '16px', textAlign: 'center' }}>Redefinir Senha Inicial</h2>
        <p style={{ color: 'rgba(255,255,255,0.7)', marginBottom: '32px', textAlign: 'center', fontSize: '14px' }}>
          Para sua segurança, é necessário cadastrar uma nova senha antes de continuar.
        </p>

        {error && (
          <div style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', padding: '12px', borderRadius: '8px', marginBottom: '24px', fontSize: '14px', textAlign: 'center' }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div>
            <label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', fontSize: '14px', marginBottom: '8px' }}>Nova Senha</label>
            <div style={{ position: 'relative' }}>
              <input required type={showPass ? "text" : "password"} value={newPass} onChange={e => setNewPass(e.target.value)} style={{ ...inputStyle, paddingRight: '48px' }} />
              <button type="button" onClick={() => setShowPass(!showPass)} style={{ position: 'absolute', top: '50%', right: '16px', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer' }}>
                {showPass ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>
          <div>
            <label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', fontSize: '14px', marginBottom: '8px' }}>Confirme a Nova Senha</label>
            <div style={{ position: 'relative' }}>
              <input required type={showPass ? "text" : "password"} value={confirmPass} onChange={e => setConfirmPass(e.target.value)} style={{ ...inputStyle, paddingRight: '48px' }} />
              <button type="button" onClick={() => setShowPass(!showPass)} style={{ position: 'absolute', top: '50%', right: '16px', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer' }}>
                {showPass ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <button disabled={loading} style={{
            width: '100%',
            padding: '14px',
            background: 'var(--primary-color)',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            fontSize: '16px',
            fontWeight: 600,
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.7 : 1,
            marginTop: '12px'
          }}>
            {loading ? 'Salvando...' : 'Salvar Nova Senha'}
          </button>
        </form>
      </div>
    </div>
  );
};

const validateCpfCnpj = (val: string) => {
  const clean = val.replace(/[^\d]+/g, '');
  if (clean.length === 11) {
    if (!!clean.match(/(\d)\1{10}/)) return false;
    let sum = 0;
    for (let i = 1; i <= 9; i++) sum += parseInt(clean.substring(i - 1, i)) * (11 - i);
    let rest = (sum * 10) % 11;
    if (rest === 10 || rest === 11) rest = 0;
    if (rest !== parseInt(clean.substring(9, 10))) return false;
    sum = 0;
    for (let i = 1; i <= 10; i++) sum += parseInt(clean.substring(i - 1, i)) * (12 - i);
    rest = (sum * 10) % 11;
    if (rest === 10 || rest === 11) rest = 0;
    if (rest !== parseInt(clean.substring(10, 11))) return false;
    return true;
  }
  if (clean.length === 14) {
    if (!!clean.match(/(\d)\1{13}/)) return false;
    let length = clean.length - 2;
    let numbers = clean.substring(0, length);
    const digits = clean.substring(length);
    let sum = 0;
    let pos = length - 7;
    for (let i = length; i >= 1; i--) {
      sum += parseInt(numbers.charAt(length - i)) * pos--;
      if (pos < 2) pos = 9;
    }
    let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
    if (result !== parseInt(digits.charAt(0))) return false;
    length = length + 1;
    numbers = clean.substring(0, length);
    sum = 0;
    pos = length - 7;
    for (let i = length; i >= 1; i--) {
      sum += parseInt(numbers.charAt(length - i)) * pos--;
      if (pos < 2) pos = 9;
    }
    result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
    if (result !== parseInt(digits.charAt(1))) return false;
    return true;
  }
  return false;
};

const CompleteOnboarding = () => {
  const { currentUser, completeOnboarding } = useAuth();
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', phone: '',
    cpfCnpj: '', birthDate: '', stringingPoint: 'Loja 1 - LaVille Mall', gender: '', address: '',
    cep: '', city: '', country: '', originClub: '', notes: '', addressNumber: '', complement: '', neighborhood: '', state: '',
    experience: '', training: '', professorId: ''
  });
  const [professors, setProfessors] = useState<any[]>([]);
  const [clubs, setClubs] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);

  useEffect(() => {
    const fetchExistingData = async () => {
      try {
        const token = localStorage.getItem('tt_auth_token');
        const API_URL = import.meta.env.VITE_API_URL || 'https://techtennis-api.vercel.app';
        const [res, profRes, settingsRes] = await Promise.all([
          fetch(`${API_URL}/api/customers`, { headers: { 'Authorization': `Bearer ${token}` } }),
          fetch(`${API_URL}/api/professors`, { headers: { 'Authorization': `Bearer ${token}` } }),
          fetch(`${API_URL}/api/settings`)
        ]);

        if (profRes.ok) {
           const data = await profRes.json();
           setProfessors(data.sort((a: any, b: any) => (a.name || '').localeCompare(b.name || '', 'pt-BR', { sensitivity: 'base' })));
        }
        if (settingsRes.ok) {
           const s = await settingsRes.json();
           if (s.clubs) setClubs(s.clubs);
        }

        let existing: any = null;
        if (currentUser?.numericId && res.ok) {
           const clients = await res.json();
           existing = clients.find((c: any) => c.numericId === currentUser.numericId);
        }

        if (existing) {
          setFormData({
            firstName: existing.name ? existing.name.split(' ')[0] : (currentUser?.name?.split(' ')[0] || ''),
            lastName: existing.name ? existing.name.split(' ').slice(1).join(' ') : (currentUser?.name?.split(' ').slice(1).join(' ') || ''),
            phone: existing.phone || currentUser?.phone || '',
            cpfCnpj: existing.cpfCnpj || '',
            birthDate: existing.birthDate ? existing.birthDate.substring(0, 10) : '',
            stringingPoint: existing.stringingPoint || 'Loja 1 - LaVille Mall',
            gender: existing.gender || '',
            address: existing.address || '',
            addressNumber: existing.addressNumber || '',
            complement: existing.complement || '',
            neighborhood: existing.neighborhood || '',
            state: existing.state || '',
            cep: existing.cep || '',
            city: existing.city || '',
            country: existing.country || '',
            originClub: existing.originClub || '',
            notes: existing.notes || '',
            experience: currentUser?.experience || '',
            training: currentUser?.training || '',
            professorId: existing.professorId || ''
          });
        } else {
          setFormData(prev => ({
            ...prev,
            firstName: currentUser?.name?.split(' ')[0] || '',
            lastName: currentUser?.name?.split(' ').slice(1).join(' ') || '',
            phone: currentUser?.phone || ''
          }));
        }
      } catch (err) {
        console.error('Erro ao buscar dados prévios:', err);
      } finally {
        setFetchingData(false);
      }
    };
    fetchExistingData();
  }, [currentUser]);

  const handleCepBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    const cep = e.target.value.replace(/\D/g, '');
    if (cep.length === 8) {
      try {
        const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
        const data = await response.json();
        if (!data.erro) {
          setFormData(prev => ({
            ...prev,
            address: data.logradouro || prev.address,
            neighborhood: data.bairro || prev.neighborhood,
            city: data.localidade || prev.city,
            state: data.uf || prev.state
          }));
        }
      } catch (error) {
        console.error('Erro ao buscar CEP:', error);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateCpfCnpj(formData.cpfCnpj)) {
      alert('Por favor, informe um CPF ou CNPJ válido.');
      return;
    }
    setLoading(true);
    try {
      await completeOnboarding(formData);
    } catch (err) {
      alert('Erro ao salvar dados do perfil.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = { width: '100%', padding: '12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', color: 'white', fontSize: '15px' };

  if (fetchingData) return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#151125', color: 'white' }}>Carregando dados...</div>;

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', background: 'linear-gradient(135deg, #1e1a3b 0%, #151125 100%)' }}>
      <div style={{ background: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(20px)', padding: '40px', borderRadius: '24px', border: '1px solid rgba(255,255,255,0.1)', width: '100%', maxWidth: '500px' }}>
        <h2 style={{ color: 'white', marginBottom: '8px', textAlign: 'center' }}>Falta pouco!</h2>
        <p style={{ color: 'rgba(255,255,255,0.7)', marginBottom: '32px', textAlign: 'center', fontSize: '14px' }}>
          {currentUser?.numericId ? 'Confirme e atualize seus dados pré-existentes.' : 'Complete seu perfil para acessar a plataforma.'}
        </p>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div><label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', fontSize: '13px', marginBottom: '6px' }}>Nome *</label>
            <input required value={formData.firstName} onChange={e => setFormData({...formData, firstName: e.target.value})} style={inputStyle} /></div>
            <div><label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', fontSize: '13px', marginBottom: '6px' }}>Sobrenome *</label>
            <input required value={formData.lastName} onChange={e => setFormData({...formData, lastName: e.target.value})} style={inputStyle} /></div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div><label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', fontSize: '13px', marginBottom: '6px' }}>CPF/CNPJ *</label>
            <input required value={formData.cpfCnpj} onChange={e => setFormData({...formData, cpfCnpj: e.target.value})} style={inputStyle} /></div>
            <div><label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', fontSize: '13px', marginBottom: '6px' }}>Celular *</label>
            <input required value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} style={inputStyle} /></div>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div><label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', fontSize: '13px', marginBottom: '6px' }}>Data de Nasc. *</label>
            <input required type="date" value={formData.birthDate} onChange={e => setFormData({...formData, birthDate: e.target.value})} style={inputStyle} /></div>
            <div><label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', fontSize: '13px', marginBottom: '6px' }}>Gênero</label>
            <select value={formData.gender} onChange={e => setFormData({...formData, gender: e.target.value})} style={inputStyle}>
              <option value="">Selecione...</option><option value="Masculino">Masculino</option><option value="Feminino">Feminino</option><option value="Outro">Outro</option>
            </select></div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '16px' }}>
             <div><label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', fontSize: '13px', marginBottom: '6px' }}>CEP *</label>
             <input required value={formData.cep} onBlur={handleCepBlur} onChange={e => setFormData({...formData, cep: e.target.value})} style={inputStyle} /></div>
             <div><label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', fontSize: '13px', marginBottom: '6px' }}>Endereço *</label>
             <input required value={formData.address} onChange={e => setFormData({...formData, address: e.target.value})} style={inputStyle} /></div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '16px' }}>
             <div><label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', fontSize: '13px', marginBottom: '6px' }}>Número *</label>
             <input required value={formData.addressNumber} onChange={e => setFormData({...formData, addressNumber: e.target.value})} style={inputStyle} /></div>
             <div><label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', fontSize: '13px', marginBottom: '6px' }}>Complemento</label>
             <input value={formData.complement} onChange={e => setFormData({...formData, complement: e.target.value})} style={inputStyle} /></div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
             <div><label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', fontSize: '13px', marginBottom: '6px' }}>Bairro</label>
             <input value={formData.neighborhood} onChange={e => setFormData({...formData, neighborhood: e.target.value})} style={inputStyle} /></div>
             <div><label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', fontSize: '13px', marginBottom: '6px' }}>Cidade</label>
             <input value={formData.city} onChange={e => setFormData({...formData, city: e.target.value})} style={inputStyle} /></div>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px' }}>
             <div><label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', fontSize: '13px', marginBottom: '6px' }}>Estado</label>
             <input value={formData.state} onChange={e => setFormData({...formData, state: e.target.value})} style={inputStyle} /></div>
          </div>

          {currentUser?.role === 'CLIENTE' && (
             <>
               <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px' }}>
                 <div><label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', fontSize: '13px', marginBottom: '6px' }}>País</label>
                 <input value={formData.country} onChange={e => setFormData({...formData, country: e.target.value})} style={inputStyle} /></div>
               </div>

               <div><label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', fontSize: '13px', marginBottom: '6px' }}>Residencial / Condomínio / Clube</label>
                 <select value={formData.originClub} onChange={e => setFormData({...formData, originClub: e.target.value})} style={inputStyle}>
                   <option value="">Selecione...</option>
                   {clubs.map(c => <option key={c} value={c}>{c}</option>)}
                 </select>
               </div>

               <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px' }}>
                 <div><label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', fontSize: '13px', marginBottom: '6px' }}>Professor / Treinador</label>
                 <select value={formData.professorId} onChange={e => setFormData({...formData, professorId: e.target.value})} style={inputStyle}>
                   <option value="">Selecione um professor...</option>
                   {professors.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                 </select></div>
               </div>

               <div><label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', fontSize: '13px', marginBottom: '6px' }}>Ponto de Encordoamento Favorito *</label>
               <select required value={formData.stringingPoint} onChange={e => setFormData({...formData, stringingPoint: e.target.value})} style={inputStyle}>
                 <option value="Loja 1 - LaVille Mall">Loja 1 - LaVille Mall</option><option value="Loja 2 - Pátio Roraima">Loja 2 - Pátio Roraima</option><option value="Avulso">Avulso</option>
               </select></div>
               
               <div><label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', fontSize: '13px', marginBottom: '6px' }}>Observações</label>
               <input value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} style={inputStyle} /></div>
             </>
          )}

          {currentUser?.role?.includes('PROFESSOR') && (
             <>
               <div><label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', fontSize: '13px', marginBottom: '6px' }}>Experiência e Certificações</label>
               <textarea value={formData.experience} onChange={e => setFormData({...formData, experience: e.target.value})} style={{ ...inputStyle, minHeight: '80px' }} /></div>
               <div><label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', fontSize: '13px', marginBottom: '6px' }}>Tipos de Treinamento</label>
               <textarea value={formData.training} onChange={e => setFormData({...formData, training: e.target.value})} style={{ ...inputStyle, minHeight: '80px' }} /></div>
             </>
          )}

          <button disabled={loading} style={{ width: '100%', padding: '14px', background: 'var(--primary-color)', color: 'white', border: 'none', borderRadius: '12px', fontSize: '16px', fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', marginTop: '16px' }}>
            {loading ? 'Salvando...' : 'Salvar Meu Perfil'}
          </button>
        </form>
      </div>
    </div>
  );
};

const Navbar = () => {
  const { logout, currentUser, updateProfile } = useAuth();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isMyProfileModalOpen, setIsMyProfileModalOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const API_URL = import.meta.env.VITE_API_URL || '';
  
  useEffect(() => {
    const token = localStorage.getItem('token') || localStorage.getItem('tt_auth_token');
    if (!currentUser || !token) return;
    const checkUnread = async () => {
      try {
        const res = await fetch(`${API_URL}/api/notifications`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setUnreadCount(data.filter((n: any) => !n.read).length);
        }
      } catch (e) {}
    };
    checkUnread();
    const interval = setInterval(checkUnread, 30000);
    return () => clearInterval(interval);
  }, [currentUser]);

  return (
    <>
      <nav className="navbar" style={{
        position: 'absolute',
        top: '20px', left: '50%', transform: 'translateX(-50%)',
        width: '100%', maxWidth: '700px', zIndex: 100,
        background: '#3A8E58', backdropFilter: 'blur(16px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '100px',
        padding: '12px 32px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '32px' }}>
            <Link to="/" style={{ display: 'flex', alignItems: 'center' }}>
              <img className="navbar-logo" src={brandLogo} alt="TechTennis Pro Stringer Logo" style={{ 
                height: '72px', 
                objectFit: 'contain',
                filter: 'brightness(0) invert(1)'
              }} />
            </Link>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '20px', position: 'relative' }}>
            {currentUser && (
              <button 
                onClick={() => setIsNotificationsOpen(true)} 
                style={{ position: 'relative', background: 'transparent', border: 'none', cursor: 'pointer', color: 'white', display: 'flex' }} 
                title="Notificações"
              >
                {unreadCount > 0 && <div style={{position: 'absolute', top: '-4px', right: '-4px', width: '12px', height: '12px', background: '#ff4d4f', borderRadius: '50%', border: '2px solid #3A8E58'}}></div>}
                <Bell size={24} />
              </button>
            )}
            <div style={{ position: 'relative' }}>
              <img src={tennisProfileImg} alt="Profile" style={{ width: 36, height: 36, cursor: 'pointer', borderRadius: '50%', objectFit: 'cover' }} onClick={() => setIsProfileOpen(!isProfileOpen)} />
              {isProfileOpen && (
                <div style={{
                  position: 'absolute', top: '40px', right: '0',
                  background: 'white', borderRadius: '12px', overflow: 'hidden',
                  boxShadow: '0 10px 40px rgba(0,0,0,0.2)', width: '180px'
                }}>
                  <button 
                    onClick={() => { setIsMyProfileModalOpen(true); setIsProfileOpen(false); }}
                    style={{ width: '100%', padding: '12px 16px', textAlign: 'left', border: 'none', background: 'transparent', cursor: 'pointer', borderBottom: '1px solid #eee', fontWeight: 600, color: '#1a1a2e' }}
                    onMouseOver={e => e.currentTarget.style.background = '#f5f5f5'}
                    onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                  >
                    Meu Perfil
                  </button>

                  <button 
                    onClick={() => { setIsProfileOpen(false); logout(); window.location.hash = '/'; window.location.reload(); }}
                    style={{ width: '100%', padding: '12px 16px', textAlign: 'left', border: 'none', background: 'transparent', cursor: 'pointer', fontWeight: 600, color: '#EF4444' }}
                    onMouseOver={e => e.currentTarget.style.background = '#fcf0f0'}
                    onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                  >
                    Sair (Logout)
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      <AnimatePresence>
        {isMyProfileModalOpen && currentUser && (
          <ProfileSettingsModal 
            currentUser={currentUser} 
            onClose={() => setIsMyProfileModalOpen(false)} 
            onUpdate={updateProfile} 
          />
        )}
      </AnimatePresence>
      <GlobalNotificationsModal 
        isOpen={isNotificationsOpen} 
        onClose={() => {
          setIsNotificationsOpen(false);
          setUnreadCount(0);
        }} 
      />
    </>
  );
};

// --- NTC Style Tile Component ---
interface HomeTileProps {
  title: string;
  subtitle?: string;
  backgroundImage: string;
  onClick: () => void;
  fullWidth?: boolean;
  backgroundPosition?: string;
  backgroundSize?: string;
  textPosition?: 'left' | 'right';
}

const HomeTile: React.FC<HomeTileProps> = ({ title, subtitle, backgroundImage, onClick, fullWidth, backgroundPosition, backgroundSize, textPosition }) => {
  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      style={{
        position: 'relative',
        borderRadius: '24px', 
        overflow: 'hidden',
        cursor: 'pointer',
        height: fullWidth ? '320px' : '260px',
        gridColumn: fullWidth ? '1 / -1' : 'span 1',
        boxShadow: '0 10px 40px rgba(0, 12, 60, 0.4)'
      }}
    >
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: "url(" + backgroundImage + ")",
        backgroundSize: backgroundSize || 'cover',
        backgroundPosition: backgroundPosition || 'center',
      }} />
      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(180deg, rgba(45, 30, 75, 0) 0%, rgba(45, 30, 75, 0) 40%, rgba(45, 30, 75, 0.9) 100%)',
      }} />
      <div style={{
        position: 'absolute', bottom: '24px', left: '24px', right: '24px',
        display: 'flex', flexDirection: 'column', gap: '4px',
        alignItems: textPosition === 'right' ? 'flex-end' : 'flex-start',
        textAlign: textPosition === 'right' ? 'right' : 'left'
      }}>
        {subtitle && <span style={{ color: 'var(--secondary-color)', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px' }}>{subtitle}</span>}
        <h3 style={{
          color: '#fff', fontSize: fullWidth ? '32px' : '24px',
          fontFamily: 'var(--font-heading)', fontWeight: 800, textTransform: 'uppercase',
          lineHeight: 1.1
        }}>{title}</h3>
      </div>
    </motion.div>
  );
};

// --- Main Home Screen ---
const Hero = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const profile = currentUser?.role || 'CLIENTE';
  
  const [hasManagedClubs, setHasManagedClubs] = useState(false);
  const API_URL = import.meta.env.VITE_API_URL || '';

  useEffect(() => {
    if (profile === 'GESTOR' || profile === 'GESTOR_COMPETICAO') {
      const token = localStorage.getItem('tt_auth_token');
      fetch(`${API_URL}/api/clubs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
          setHasManagedClubs(true);
        }
      })
      .catch(console.error);
    }
  }, [profile, API_URL]);

  return (
    <div style={{ 
      minHeight: '100vh', 
      padding: '160px 5% 60px',
      maxWidth: '1200px',
      margin: '0 auto',
      width: '100%'
    }}>
      
      <h1 style={{ fontSize: '32px', marginBottom: '8px', fontWeight: 800, color: 'var(--text-primary)', fontFamily: 'var(--font-heading)' }}>
        BEM-VINDO A TECHTENNIS
      </h1>
      <p style={{ fontSize: '16px', color: 'var(--text-secondary)', marginBottom: '32px' }}>
        Logado como {currentUser?.name} | Perfil: <strong style={{ color: 'var(--primary-color)' }}>{profile}</strong>
      </p>

      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
        gap: '24px' 
      }}>
        {/* ADMIN E ENCORDOADOR */}
        {(profile === 'ADMIN' || profile.includes('ENCORDOADOR')) && (
          <>
            {profile === 'ADMIN' && (
              <>

                <HomeTile 
                  title="Configurações de Usuários" subtitle="Gestão de Acessos"
                  backgroundImage={gestaoUsuariosImg} backgroundPosition="center 40%"
                  onClick={() => navigate('/users')}
                />
                  <HomeTile 
                    title="Logs do Sistema" subtitle="Auditoria"
                    backgroundImage="https://images.unsplash.com/photo-1555949963-ff9fe0c870eb?q=80&w=1500&auto=format&fit=crop"
                    backgroundPosition="center 40%"
                    onClick={() => navigate('/logs')}
                  />
                  <HomeTile 
                    title="Monitoramento de Preços" subtitle="Scraping & KPIs"
                    backgroundImage={pricingBgImg}
                    backgroundPosition="center 40%"
                    onClick={() => navigate('/pricing')}
                  />
                </>
              )}
            <HomeTile 
              title="Gestão de Encordoamento" subtitle="Acesso Master" { ...(profile !== 'ADMIN' ? { fullWidth: true } : {}) }
              backgroundImage={ernestoImg} backgroundPosition="left 35%" textPosition="right"
              onClick={() => navigate('/stringer')}
            />
            <HomeTile 
              title="Coleta de Raquetes" subtitle="Controle e Extrato"
              backgroundImage={racketCollectionImg} backgroundPosition="center 30%"
              onClick={() => navigate('/racket-collection', { state: { role: 'ENCORDOADOR' } })}
            />
            <HomeTile 
              title="Gestão de Aulas" subtitle="Dashboard"
              backgroundImage="https://images.unsplash.com/photo-1554068865-24cecd4e34b8?q=80&w=800&auto=format&fit=crop"
              onClick={() => navigate('/classes-professor')}
            />
            {profile === 'ADMIN' && (
              <HomeTile 
                title="Plano de Treinos" subtitle="Desenvolvimento 360 Graus"
                backgroundImage={treinoDesenvolvimentoImg} backgroundPosition="center 30%"
                onClick={() => navigate('/training-plan')}
              />
            )}
            <HomeTile 
              title="Aulas Avulsas" subtitle="Visão Professor"
              backgroundImage="https://images.unsplash.com/photo-1530915534664-4ac6423816b7?q=80&w=1500&auto=format&fit=crop"
              onClick={() => navigate('/professor-single-class')}
            />
            <HomeTile 
              title="Buscar Aula Avulsa" subtitle="Visão Aluno"
              backgroundImage="https://images.unsplash.com/photo-1530915534664-4ac6423816b7?q=80&w=1500&auto=format&fit=crop"
              onClick={() => navigate('/customer-single-class')}
            />
            <HomeTile 
              title="Agenda Aberta" subtitle="Horários Disponíveis"
              backgroundImage={agendaAbertaImg}
              onClick={() => navigate('/open-agenda', { state: { role: 'ENCORDOADOR' } })}
            />
            <>
              {/* ADMIN - Buscar Amistoso */}
              <HomeTile 
                title="Buscar Amistoso" subtitle="Radar de Adversários"
                backgroundImage={buscarAmistosoImg} backgroundPosition="center 30%"
                onClick={() => navigate('/friendly-match')}
              />
              <HomeTile 
                title="Competições" subtitle="Gestão Geral"
                backgroundImage={torneiosNewImg} backgroundPosition="center center"
                onClick={() => navigate('/competitions', { state: { view: 'manager' } })}
              />
            </>
          </>
        )}

        {/* PROFESSOR DE TÊNIS E PROFESSOR PREMIUM */}
        {(profile === 'PROFESSOR' || profile === 'PROFESSOR_PREMIUM') && (
          <>
            <HomeTile 
              title="Coleta de Raquetes" subtitle="Apoio aos Alunos" fullWidth
              backgroundImage={racketCollectionImg} backgroundPosition="center 30%"
              onClick={() => navigate('/racket-collection', { state: { role: 'PROFESSOR' } })}
            />
            <HomeTile 
              title="Gestão de Aulas" subtitle="Agenda Completa"
              backgroundImage="https://images.unsplash.com/photo-1554068865-24cecd4e34b8?q=80&w=800&auto=format&fit=crop"
              onClick={() => navigate('/classes-professor')}
            />
            <HomeTile 
              title="Plano de Treinos" subtitle="Desenvolvimento 360 Graus"
              backgroundImage={treinoDesenvolvimentoImg} backgroundPosition="center 30%"
              onClick={() => navigate('/training-plan')}
            />
            <HomeTile 
              title="Meu Encordoamento" subtitle="Equipamento Pessoal"
              backgroundImage={ernestoImg} backgroundPosition="left 35%" textPosition="right"
              onClick={() => navigate('/feedback')}
            />
            <HomeTile 
              title="Aulas Avulsas" subtitle="Novas Solicitações"
              backgroundImage="https://images.unsplash.com/photo-1530915534664-4ac6423816b7?q=80&w=1500&auto=format&fit=crop"
              onClick={() => navigate('/professor-single-class')}
            />
            <HomeTile 
              title="Agenda Aberta" subtitle="Divulgue Seus Horários"
              backgroundImage={agendaAbertaImg}
              onClick={() => navigate('/open-agenda', { state: { role: 'PROFESSOR' } })}
            />
            <HomeTile 
              title="Buscar Amistoso" subtitle="Radar de Adversários"
              backgroundImage={buscarAmistosoImg} backgroundPosition="center center"
              onClick={() => navigate('/friendly-match')}
            />
          </>
        )}

        {/* CLIENTE FINAL E GESTOR DE COMPETICAO */}
        {(profile === 'CLIENTE' || profile === 'GESTOR_COMPETICAO') && (
          <>

            <HomeTile 
              title="Meu Encordoamento" subtitle="Histórico & Feedback"
              backgroundImage={ernestoImg} backgroundPosition="left 35%" textPosition="right"
              onClick={() => navigate('/feedback')}
            />
            <HomeTile 
              title="Gestão de Aulas" subtitle="Meus Agendamentos"
              backgroundImage="https://images.unsplash.com/photo-1554068865-24cecd4e34b8?q=80&w=800&auto=format&fit=crop"
              onClick={() => navigate('/classes-customer')}
            />
            <HomeTile 
              title="Buscar Aula Avulsa" subtitle="Treine Hoje"
              backgroundImage="https://images.unsplash.com/photo-1530915534664-4ac6423816b7?q=80&w=1500&auto=format&fit=crop"
              onClick={() => navigate('/customer-single-class')}
            />
            <HomeTile 
              title="Agenda Aberta" subtitle="Horários Disponíveis"
              backgroundImage={agendaAbertaImg}
              onClick={() => navigate('/open-agenda', { state: { role: 'CLIENTE' } })}
            />
            <>
              <HomeTile 
                title="Buscar Amistoso" subtitle="Radar de Adversários"
                backgroundImage={buscarAmistosoImg} backgroundPosition="center 30%"
                onClick={() => navigate('/friendly-match')}
              />
              <HomeTile 
                title="Competições" subtitle={profile === 'GESTOR_COMPETICAO' ? "Gestão de Competições" : "Rankings, Torneios e Partidas"}
                backgroundImage={torneiosNewImg} backgroundPosition="center center"
                onClick={() => navigate('/competitions')}
              />
            </>
          </>
        )}

        {/* ADMIN E GESTOR - Gestão de Clubes (Final) */}
        {(profile === 'ADMIN' || ((profile === 'GESTOR' || profile === 'GESTOR_COMPETICAO') && hasManagedClubs)) && (
          <HomeTile 
            title="Gestão de Clubes" subtitle="Quadras e Horários"
            backgroundImage={gestaoClubesImg} 
            backgroundPosition="center 20%"
            onClick={() => navigate('/clubs')}
          />
        )}
        
        {/* --- FUNCIONALIDADES GLOBAIS --- */}
        <HomeTile 
          title="Giro de Torneios Profissionais" 
          subtitle="Resultados Globais ATP & ITF"
          backgroundImage={giroBackground} 
          backgroundPosition="center center"
          onClick={() => navigate('/global-results')}
        />
      </div>

    </div>
  );
};

function App() {
  useEffect(() => {
    const handleWheel = () => {
      if (document.activeElement?.tagName === 'INPUT' && (document.activeElement as HTMLInputElement).type === 'number') {
        (document.activeElement as HTMLElement).blur();
      }
    };
    document.addEventListener('wheel', handleWheel, { passive: false });
    return () => document.removeEventListener('wheel', handleWheel);
  }, []);

  const searchParams = new URLSearchParams(window.location.search);
  const hashParams = new URLSearchParams(window.location.hash.split('?')[1] || '');
  const resetToken = searchParams.get('token') || hashParams.get('token');
  const isResetPath = window.location.pathname.includes('/reset-password') || window.location.hash.includes('/reset-password') || window.location.search.includes('token=') || window.location.hash.includes('token=');

  if (isResetPath && resetToken) {
    return <ResetPasswordView token={resetToken} />;
  }

  const { currentUser } = useAuth();
  if (!currentUser) return <LoginView />;

  if (currentUser.mustChangePassword) {
    return <ForceChangePassword />;
  }

  if (currentUser.isProfileComplete === false) {
    return <CompleteOnboarding />;
  }

  return (
    <>
      <div className="page-container">
        
        {/* Tennis Court Background SVG */}
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%) rotate(-15deg)',
          opacity: 0.15,
          zIndex: -1,
          pointerEvents: 'none',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '150vw',
          height: '150vh',
          overflow: 'hidden'
        }}>
          <svg viewBox="-20 -20 400 820" style={{ width: '80%', height: '150%', minWidth: '800px' }} stroke="#ffffff" strokeWidth="3" fill="none">
             {/* Outer Boundary (Doubles) */}
             <rect x="0" y="0" width="360" height="780" />
             {/* Singles sidelines */}
             <line x1="45" y1="0" x2="45" y2="780" />
             <line x1="315" y1="0" x2="315" y2="780" />
             {/* Net */}
             <line x1="-20" y1="390" x2="380" y2="390" strokeWidth="6" stroke="#ffffff" opacity="0.8" />
             {/* Service Lines */}
             <line x1="45" y1="180" x2="315" y2="180" />
             <line x1="45" y1="600" x2="315" y2="600" />
             {/* Center Service Line */}
             <line x1="180" y1="180" x2="180" y2="600" />
             {/* Center Marks */}
             <line x1="180" y1="0" x2="180" y2="15" />
             <line x1="180" y1="765" x2="180" y2="780" />
          </svg>
        </div>

        <Navbar />
        <main>
          <Routes>
            <Route path="/" element={<Hero />} />
            <Route path="/stringer" element={<StringerDashboard />} />
            <Route path="/feedback" element={<CustomerFeedback />} />
            <Route path="/racket-collection" element={<RacketCollection />} />
            <Route path="/classes-professor" element={<ClassManagementProfessor />} />
            <Route path="/classes-customer" element={<ClassManagementCustomer />} />
            <Route path="/customer-single-class" element={<CustomerSingleClass />} />
            <Route path="/professor-single-class" element={<ProfessorSingleClass />} />
            <Route path="/open-agenda" element={<OpenAgenda />} />
            <Route path="/friendly-match" element={<FriendlyMatchRadar />} />
            <Route path="/users" element={<UserManagement />} />
            <Route path="/clubs" element={<ClubManagement />} />
            <Route path="/logs" element={<LogsView />} />
            <Route path="/competitions" element={<CompetitionsHub />} />
            <Route path="/classifications/:id" element={<ClassificationView />} />
            <Route path="/rankings" element={<CompetitionListView type="RANKING" />} />
            <Route path="/rankings/:id" element={<RankingView />} />
            <Route path="/tournaments" element={<CompetitionListView type="TOURNAMENT" />} />
            <Route path="/tournaments/:id" element={<TournamentView />} />
            <Route path="/pricing" element={<PricingDashboard />} />
            <Route path="/global-results" element={<GlobalResults />} />
            <Route path="/training-plan" element={<TrainingPlanManager />} />
          </Routes>
        </main>
      </div>
      {(currentUser.role === 'PROFESSOR' || currentUser.role === 'PROFESSOR_PREMIUM' || currentUser.role === 'ADMIN') && <GlobalRadarNotification />}
      <FriendlyMatchGlobalNotification />
    </>
  );
}

const WrappedApp = () => (
  <AuthProvider>
      <Toaster position="top-right" />
    <Router>
      <App />
    </Router>
  </AuthProvider>
)

export default WrappedApp;
